import type { MatchEvent } from './protocol';

export type AnnouncerTone = 'alert' | 'success' | 'system';

export type AnnouncerCue = {
  id: string;
  label: string;
  tone: AnnouncerTone;
  priority: number;
};

type AnnouncerEventLike = {
  id?: string | null;
  message?: string | null;
  category?: MatchEvent['category'] | null;
  priority?: number | null;
};

const POWERED_BASE_SYSTEM_PATTERN = /\b(generator|sensor|shield door|turret network)\b/i;
const POWERED_BASE_OUTAGE_PATTERN = /\b(offline|down|destroyed|disabled|lost|breach|outage|cut|shutdown|powered off|power off)\b/i;
const POWERED_BASE_RESTORED_PATTERN = /\b(restored|online|repaired|reactivated|powered up|back online)\b/i;

function poweredBaseState(message: string): 'outage' | 'restored' | null {
  if (!POWERED_BASE_SYSTEM_PATTERN.test(message)) {
    return null;
  }

  if (POWERED_BASE_OUTAGE_PATTERN.test(message)) {
    return 'outage';
  }

  if (POWERED_BASE_RESTORED_PATTERN.test(message)) {
    return 'restored';
  }

  return null;
}

function eventTone(event: Pick<AnnouncerEventLike, 'category' | 'message'>): AnnouncerTone {
  const message = event.message ?? '';
  const poweredBase = poweredBaseState(message);
  if (poweredBase === 'outage') {
    return 'alert';
  }
  if (poweredBase === 'restored') {
    return 'success';
  }
  if (event.category === 'critical' || /carrier|threat|breach|eliminated/i.test(message)) {
    return 'alert';
  }
  if (event.category === 'objective' || /captured|returned|complete|wins|loadout/i.test(message)) {
    return 'success';
  }
  return 'system';
}

function eventPriority(event: Pick<AnnouncerEventLike, 'priority' | 'category' | 'message'>): number {
  const message = event.message ?? '';
  const poweredBase = poweredBaseState(message);
  if (poweredBase === 'outage') {
    return 500;
  }
  if (poweredBase === 'restored') {
    return 440;
  }
  if (typeof event.priority === 'number' && Number.isFinite(event.priority)) {
    return event.priority;
  }
  if (event.category === 'critical') {
    return 420;
  }
  if (event.category === 'objective') {
    return 360;
  }
  if (/captured|returned|wins|complete/i.test(message)) {
    return 320;
  }
  return 180;
}

function summarizeMessage(message: string): string {
  if (message.length <= 56) {
    return message;
  }
  return `${message.slice(0, 55)}…`;
}

export function deriveAnnouncerCue(events: AnnouncerEventLike[]): AnnouncerCue | null {
  const normalizedEvents = events.filter((event) => typeof event.message === 'string' && event.message.length > 0);
  if (!normalizedEvents.length) {
    return null;
  }

  const candidates = normalizedEvents.slice(-6).map((event, index) => ({
    event,
    priority: eventPriority(event),
    tone: eventTone(event),
    fallbackId: `cue-${index}`,
  }));
  candidates.sort((left, right) => right.priority - left.priority);
  const top = candidates[0];
  if (!top) {
    return null;
  }

  return {
    id: top.event.id ?? top.fallbackId,
    label: summarizeMessage(top.event.message ?? ''),
    tone: top.tone,
    priority: top.priority,
  };
}

export function deriveKillStreakLabel(killCount: number): string {
  if (killCount <= 1) return '';
  if (killCount === 2) return 'DOUBLE KILL';
  if (killCount === 3) return 'TRIPLE KILL';
  if (killCount === 4) return 'QUAD KILL';
  return 'KILLING SPREE';
}

export function announcerToneProfile(tone: AnnouncerTone): { frequency: number; durationMs: number } {
  if (tone === 'alert') {
    return { frequency: 352, durationMs: 170 };
  }
  if (tone === 'success') {
    return { frequency: 622, durationMs: 140 };
  }
  return { frequency: 480, durationMs: 110 };
}
